import * as React from 'react';
import { SelectField } from 'react-admin';
import { required } from 'react-admin';

import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    Show,
    SimpleShowLayout,
    Edit,
    usePermissions,
    useRecordContext,
    useTranslate,
    Toolbar,
    SaveButton,
    ReferenceField,
    Filter,
    ReferenceInput,
} from 'react-admin';
import { Chip, Box } from '@mui/material';
import { ROLES } from '../../../constants';

const RequestTypeChoices = [
    { id: 'ADVANCE_PAYMENT', name: 'resources.request.fields.type_advance_payment' },
    { id: 'EMPLOYMENT_LETTER', name: 'resources.request.fields.type_employment_letter' },
    { id: 'LEAVE', name: 'resources.request.fields.type_leave' },
    { id: 'SALARY_SLIP', name: 'resources.request.fields.type_salary_slip' },
    { id: 'LOAN', name: 'resources.request.fields.type_loan' },
    { id: 'SALARY_SLIP_DISCREPANCY', name: 'resources.request.fields.type_salary_slip_discrepancy' },
    { id: 'OTHER', name: 'resources.request.fields.type_other' },
];

const RequestStatusChoices = [
    { id: 'PENDING', name: 'resources.request.fields.status_pending' },
    { id: 'APPROVED', name: 'resources.request.fields.status_approved' },
    { id: 'REJECTED', name: 'resources.request.fields.status_rejected' },
];

const RequestStatusField = (props) => {
    const record = useRecordContext(props);
    const translate = useTranslate();
    if (!record || !record.status) return null;

    let sx = { fontWeight: 'bold' };
    if (record.status === 'PENDING') sx = { ...sx, bgcolor: '#fff3e0', color: '#e65100' };
    if (record.status === 'APPROVED') sx = { ...sx, bgcolor: '#e8f5e9', color: '#1b5e20' };
    if (record.status === 'REJECTED') sx = { ...sx, bgcolor: '#ffebee', color: '#b71c1c' };

    const statusChoice = RequestStatusChoices.find(c => c.id === record.status);
    const label = statusChoice ? translate(statusChoice.name) : record.status;

    return <Chip label={label} sx={sx} size="small" />;
};

const RequestFilter = (props) => (
    <Filter {...props}>
        <SelectInput source="status" label="وضعیت" choices={RequestStatusChoices} alwaysOn />
        <ReferenceInput label="کاربر" source="owner" reference="adminUsers" alwaysOn >
            <SelectInput optionText="name" />
        </ReferenceInput>
    </Filter>
);

export const RequestList = (props) => {
    const { permissions } = usePermissions();
    const isAdmin = permissions?.includes(ROLES.HR_ADMIN) || permissions?.includes(ROLES.HR_PANEL_ADMIN) || permissions?.includes(ROLES.ROOT) || permissions?.includes(ROLES.SUPER_ADMIN);
    return (
        <List {...props} filters={isAdmin ? <RequestFilter /> : null} sort={{ field: 'createdAt', order: 'DESC' }} bulkActionButtons={false} perPage={50}>
            <Datagrid rowClick={isAdmin ? "edit" : "show"}>
                <TextField source="title" label="resources.request.fields.title" />
                <SelectField source="type" choices={RequestTypeChoices} label="resources.request.fields.type" />
                <RequestStatusField source="status" label="resources.request.fields.status" />
                <ReferenceField link={null} label="از طرف " source="owner" reference="adminUsers" >
                    <TextField source="name" style={{ display: 'inline-grid', float: 'right', marginLeft: '10px' }} />
                </ReferenceField>
                <TextField source="adminResponse" label="resources.request.fields.adminResponse" />

                <DateField source="createdAt" label="resources.request.fields.createdAt" locales="fa-IR" showTime />
            </Datagrid>
        </List>
    );
};

export const RequestCreate = (props) => (
    <Create {...props}>
        <SimpleForm redirect="list">
            <TextInput source="title" label="resources.request.fields.title" validate={required()} style={{ maxWidth: 500 }} />
            <SelectInput source="type" choices={RequestTypeChoices} label="resources.request.fields.type" validate={required()} style={{ maxWidth: 500 }} />
            <TextInput source="description" label="resources.request.fields.description" multiline rows={4} style={{ maxWidth: 600 }} />
        </SimpleForm>
    </Create>
);


export const RequestShow = (props) => (
    <Show {...props} actions={null}>
        <SimpleShowLayout>
            <TextField source="title" label="resources.request.fields.title" />
            <SelectField source="type" choices={RequestTypeChoices} label="resources.request.fields.type" />
            <RequestStatusField source="status" label="resources.request.fields.status" />
            <TextField source="description" label="resources.request.fields.description" />
            <TextField source="adminResponse" label="resources.request.fields.adminResponse" />
            <DateField source="createdAt" label="resources.request.fields.createdAt" locales="fa-IR" showTime />
        </SimpleShowLayout>
    </Show>
);

const RequestEditToolbar = (props) => {
    const { permissions } = usePermissions();
    const isAdmin = permissions?.includes(ROLES.HR_ADMIN) || permissions?.includes(ROLES.HR_PANEL_ADMIN) || permissions?.includes(ROLES.ROOT) || permissions?.includes(ROLES.SUPER_ADMIN);
    // We can add custom buttons here if needed, but for now we stick to the form fields
    // as React Admin's SaveButton handles the submission. 
    // If the user *strictly* wants buttons to change status, we can add them, 
    // but a SelectInput is often cleaner for data entry. 
    // However, the user asked for "Two buttons or a dropdown". 
    // The current implementation HAS a dropdown. 
    // I will add a Toolbar to be safe and clearer.
    return (
        <Toolbar {...props}>
            {isAdmin ? <SaveButton /> : <SaveButton disabled />}
        </Toolbar>
    );
};

const RequestEditActions = () => null;

export const RequestEdit = (props) => {
    const { permissions } = usePermissions();
    const isAdmin = permissions?.includes(ROLES.HR_ADMIN) || permissions?.includes(ROLES.HR_PANEL_ADMIN) || permissions?.includes(ROLES.ROOT) || permissions?.includes(ROLES.SUPER_ADMIN);

    return (
        <Edit {...props}  >
            <SimpleForm redirect={false} submitOnEnter={false} toolbar={<RequestEditToolbar />}>
                <TextInput source="title" label="resources.request.fields.title" disabled style={{ maxWidth: 500 }} />
                <SelectInput source="type" choices={RequestTypeChoices} label="resources.request.fields.type" disabled style={{ maxWidth: 500 }} />
                <TextInput source="description" label="resources.request.fields.description" multiline rows={4} disabled style={{ maxWidth: 600 }} />
                <Box mt={2}>
                    <DateField source="createdAt" label="resources.request.fields.createdAt" showTime />
                </Box>

                {isAdmin ? (
                    <Box mt={4} p={2} border="1px solid #ccc" borderRadius={1} bgcolor="#fafafa" style={{ maxWidth: 600 }}>
                        <h3>پاسخ مدیریت</h3>
                        <SelectInput
                            source="status"
                            choices={RequestStatusChoices}
                            label="وضعیت"
                            style={{ maxWidth: 300 }}
                        />
                        <TextInput
                            source="adminResponse"
                            label="پاسخ مدیریت"
                            multiline
                            rows={3}
                            style={{ maxWidth: 600, width: '100%' }}
                        />
                    </Box>
                ) : (
                    <Box mt={2}>
                        <Box mb={1}>
                            <strong>وضعیت : </strong> <RequestStatusField source="status" />
                        </Box>
                        پاسخ مدیریت :
                        <TextField source="adminResponse" label="پاسخ مدیریت" />

                    </Box>
                )}
            </SimpleForm>
        </Edit>
    );
};
